import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { api } from '../api/client'
import type { AuditEvent, InvoiceOut } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { PageHeader } from '../components/Layout'
import { ProofOfDelivery } from '../components/ProofOfDelivery'
import { Panel } from '../components/States'
import { StatusChip } from '../components/StatusChip'
import { count, formatDate, formatDateTime, fullName, humanise, money, num } from '../lib/format'

export function InvoiceDetailPage() {
  const { invoiceId = '' } = useParams()
  // Coordinators open this page from the delivery lookup; the customer and
  // salesperson drill-downs are admin pages, so they get plain text instead.
  const { isAdmin } = useAuth()

  const query = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => api.get<InvoiceOut>(`/invoices/${encodeURIComponent(invoiceId)}`),
  })

  const audit = useQuery({
    queryKey: ['invoice-audit', invoiceId],
    queryFn: () => api.get<AuditEvent[]>(`/invoices/${encodeURIComponent(invoiceId)}/audit-trail`),
  })

  const invoice = query.data
  const deliveries = (audit.data ?? []).filter((event) => event.event_type === 'delivery_completed')
  const itemsGross = (invoice?.items ?? []).reduce(
    (sum, item) => sum + num(item.price) * item.qty,
    0,
  )
  const outstanding = num(invoice?.grand_total) - num(invoice?.total_paid)

  // Two different things both called "discount":
  //  - line discount is the markdown off retail, ALREADY inside item.price
  //    (verified: 93% of discounted lines equal (retail - price) x qty), so it
  //    must never be subtracted again;
  //  - total_discount comes off the invoice and does move the money.
  const lineDiscount = (invoice?.items ?? []).reduce((sum, item) => sum + num(item.discount), 0)
  const invoiceDiscount = num(invoice?.total_discount)
  const totalSaved = lineDiscount + invoiceDiscount

  return (
    <>
      <PageHeader
        title={invoice?.hike_invoice_number ?? invoice?.reference ?? 'Invoice'}
        subtitle={
          invoice
            ? `${humanise(invoice.source)} · ${formatDate(invoice.finalize_date ?? invoice.created_at)}`
            : undefined
        }
      >
        <Link className="btn" to="/invoices">
          {isAdmin ? '← All invoices' : '← Lookup'}
        </Link>
      </PageHeader>

      <div className="content stack">
        <Panel isLoading={query.isPending} error={query.error}>
          {invoice ? (
            <>
              <div className="row">
                <StatusChip status={invoice.status} />
                <StatusChip status={invoice.fulfillment_status} />
                {invoice.status === 'layby' && outstanding > 0 ? (
                  <span className="chip purple">{money(outstanding)} outstanding</span>
                ) : null}
              </div>

              {invoice.status === 'parked' ? (
                <div className="alert info">
                  <strong>Nothing paid yet.</strong> A parked sale holds no stock and doesn't
                  count as a sale, since it may still be voided in HIKE. The first payment
                  makes it a layby; full payment makes it a sale.
                </div>
              ) : null}

              <div className="grid cols-2">
                <div className="card">
                  <div className="card-head">
                    <h2>Details</h2>
                  </div>
                  <div className="card-body">
                    <dl className="dl">
                      <dt>Customer</dt>
                      <dd>
                        {isAdmin ? (
                          <Link to={`/customers/${invoice.customer_id}`}>
                            {fullName(invoice.customer_first_name, invoice.customer_last_name)}
                          </Link>
                        ) : (
                          fullName(invoice.customer_first_name, invoice.customer_last_name)
                        )}
                      </dd>
                      <dt>Phone</dt>
                      <dd>{invoice.customer_phone ?? '—'}</dd>
                      <dt>Salesperson</dt>
                      <dd>
                        {invoice.sales_person_id && isAdmin ? (
                          <Link to={`/invoices?sales_person=${invoice.sales_person_id}`}>
                            {fullName(
                              invoice.sales_person_first_name,
                              invoice.sales_person_last_name,
                            )}
                          </Link>
                        ) : invoice.sales_person_first_name ? (
                          fullName(invoice.sales_person_first_name, invoice.sales_person_last_name)
                        ) : (
                          '—'
                        )}
                      </dd>
                      <dt>Branch</dt>
                      <dd>{invoice.outlet_name ?? '—'}</dd>
                      <dt>Reference</dt>
                      <dd>{invoice.reference}</dd>
                      <dt>HIKE sale id</dt>
                      <dd>{invoice.hike_sale_id ?? '—'}</dd>
                      <dt>Register</dt>
                      <dd>{invoice.register_name ?? '—'}</dd>
                      <dt>Payment type</dt>
                      <dd>{humanise(invoice.payment_type)}</dd>
                      <dt>Completed</dt>
                      <dd>{formatDateTime(invoice.finalize_date)}</dd>
                      <dt>Imported</dt>
                      <dd>{formatDateTime(invoice.created_at)}</dd>
                    </dl>
                    {invoice.notes ? (
                      <div className="alert info" style={{ marginTop: 12 }}>
                        {invoice.notes}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="card">
                  <div className="card-head">
                    <h2>Money</h2>
                    {totalSaved > 0 ? (
                      <span className="chip green">{money(totalSaved)} discount</span>
                    ) : null}
                  </div>
                  <div className="card-body">
                    <dl className="dl">
                      <dt>Items gross</dt>
                      <dd>{money(itemsGross)}</dd>
                      <dt>Subtotal (ex. tax)</dt>
                      <dd>{money(invoice.subtotal)}</dd>
                      {invoiceDiscount > 0 ? (
                        <>
                          <dt>Invoice discount</dt>
                          <dd>−{money(invoiceDiscount)}</dd>
                        </>
                      ) : null}
                      <dt>Tax</dt>
                      <dd>{money(invoice.total_tax)}</dd>
                      <dt>Grand total</dt>
                      <dd>
                        <strong>{money(invoice.grand_total)}</strong>
                      </dd>
                      <dt>Paid</dt>
                      <dd>{money(invoice.total_paid)}</dd>
                      <dt>Outstanding</dt>
                      <dd>{money(Math.max(0, outstanding))}</dd>
                    </dl>

                    {lineDiscount > 0 ? (
                      <div className="alert info" style={{ marginTop: 12 }}>
                        <strong>{money(lineDiscount)} off retail</strong> across the items — the
                        prices below are already marked down, so this is not subtracted again.
                      </div>
                    ) : null}

                    {/*
                      HIKE's subTotal is gross — sum(price × qty) — so these two
                      should agree. A mismatch is the signature of the
                      frozen-totals bug the hourly sweep heals.
                    */}
                    {Math.abs(itemsGross - num(invoice.subtotal)) > 1 ? (
                      <div className="alert warn" style={{ marginTop: 12 }}>
                        Items gross and subtotal disagree by{' '}
                        <strong>{money(Math.abs(itemsGross - num(invoice.subtotal)))}</strong>. This
                        invoice may be waiting on the money reconcile sweep.
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              {deliveries.map((event, index) => (
                <div className="card" key={`${event.timestamp}-${index}`}>
                  <div className="card-head">
                    <h2>Proof of delivery</h2>
                    <span className="hint">
                      {deliveries.length > 1 ? `Run ${index + 1} of ${deliveries.length} · ` : ''}
                      {event.description}
                    </span>
                  </div>
                  <div className="card-body">
                    <ProofOfDelivery event={event} />
                  </div>
                </div>
              ))}

              <div className="card">
                <div className="card-head">
                  <h2>Payment</h2>
                  <span className="hint">{humanise(invoice.payment_type)}</span>
                </div>
                <div className="card-body flush">
                  <Panel
                    isLoading={false}
                    error={null}
                    isEmpty={invoice.payment_splits.length === 0}
                    emptyText="No payment breakdown recorded for this invoice."
                  >
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>Method</th>
                            <th className="num">Amount</th>
                            <th className="num">Share</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoice.payment_splits.map((split, index) => {
                            const paid = num(invoice.total_paid)
                            const share = paid ? (num(split.amount) / paid) * 100 : 0
                            return (
                              <tr key={`${split.payment_option_name}-${index}`}>
                                <td className="strong">{split.payment_option_name}</td>
                                <td className="num">{money(split.amount)}</td>
                                <td className="num cell-sub">{share.toFixed(0)}%</td>
                              </tr>
                            )
                          })}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td className="strong">Total paid</td>
                            <td className="num strong">{money(invoice.total_paid)}</td>
                            <td />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </Panel>
                </div>
              </div>

              <div className="card">
                <div className="card-head">
                  <h2>Items</h2>
                  <span className="hint">{count(invoice.items.length)} lines</span>
                </div>
                <div className="card-body flush">
                  <Panel isLoading={false} error={null} isEmpty={invoice.items.length === 0}>
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>Item</th>
                            <th className="num">Qty</th>
                            <th className="num">Retail</th>
                            <th className="num">Price</th>
                            <th className="num">Off retail</th>
                            <th className="num">Tax</th>
                            <th className="num">Line total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoice.items.map((item) => (
                            <tr key={item.id}>
                              <td>
                                {item.name}
                                {item.model ? <div className="cell-sub">{item.model}</div> : null}
                                {item.fulfillment_stopped ? (
                                  <div>
                                    <span className="chip grey">Fulfilment stopped</span>
                                  </div>
                                ) : null}
                                {item.hike_product_not_found ? (
                                  <div>
                                    <span className="chip amber">Not found in HIKE</span>
                                  </div>
                                ) : null}
                              </td>
                              <td className="num">{count(item.qty)}</td>
                              <td className="num cell-sub">
                                {item.retail_price === null ? '—' : money(item.retail_price)}
                              </td>
                              <td className="num">{money(item.price)}</td>
                              <td className="num">
                                {num(item.discount) > 0 ? (
                                  <span className="chip green">{money(item.discount)}</span>
                                ) : (
                                  <span className="cell-sub">—</span>
                                )}
                              </td>
                              <td className="num">{money(item.tax)}</td>
                              <td className="num strong">
                                {item.line_total === null
                                  ? money(num(item.price) * item.qty)
                                  : money(item.line_total)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Panel>
                </div>
              </div>

              <div className="card">
                <div className="card-head">
                  <h2>Audit trail</h2>
                </div>
                <div className="card-body flush">
                  <Panel
                    isLoading={audit.isPending}
                    error={audit.error}
                    isEmpty={(audit.data?.length ?? 0) === 0}
                    emptyText="No recorded events."
                  >
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th className="num">When</th>
                            <th>Event</th>
                            <th>Who</th>
                            <th>Detail</th>
                          </tr>
                        </thead>
                        <tbody>
                          {audit.data?.map((event, index) => (
                            <tr key={`${event.event_type}-${event.timestamp}-${index}`}>
                              <td className="num cell-sub">{formatDateTime(event.timestamp)}</td>
                              <td>
                                <StatusChip status={event.event_type} />
                                <div>{event.description}</div>
                              </td>
                              <td className="cell-sub">{event.actor ?? '—'}</td>
                              <td className="cell-sub">
                                {event.detail ?? ''}
                                {event.recipient_name ? (
                                  <div>Received by {event.recipient_name}</div>
                                ) : null}
                                {event.items?.length ? (
                                  <div>
                                    {event.items
                                      .map((item) => `${item.name} ×${item.qty}`)
                                      .join(', ')}
                                  </div>
                                ) : null}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Panel>
                </div>
              </div>
            </>
          ) : null}
        </Panel>
      </div>
    </>
  )
}
